import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';

/**
 * Debug Information Component
 * Shows connection details and console logs on-screen for mobile debugging
 *
 * Usage: Add <DebugInfo visible={debugVisible} setVisible={setDebugVisible} /> to any component, then tap 5 times on the Gibbons logo to toggle
 */
export default function DebugInfo({ visible, setVisible }) {
  const [logs, setLogs] = useState([]);
  const [collapsed, setCollapsed] = useState(true); // Start collapsed by default

  // Intercept console.log and console.error
  useEffect(() => {
    const originalLog = console.log;
    const originalError = console.error;

    console.log = (...args) => {
      originalLog(...args);
      setLogs(prev => [...prev.slice(-20), { type: 'log', msg: args.join(' '), time: new Date().toLocaleTimeString() }]);
    };

    console.error = (...args) => {
      originalError(...args);
      setLogs(prev => [...prev.slice(-20), { type: 'error', msg: args.join(' '), time: new Date().toLocaleTimeString() }]);
    };

    return () => {
      console.log = originalLog;
      console.error = originalError;
    };
  }, []);


  // Test backend connection
  const testConnection = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/active-sessions`);
      const data = await response.json();
      setLogs(prev => [...prev, {
        type: 'log',
        msg: `✅ Backend connected! Got ${data.length} sessions`,
        time: new Date().toLocaleTimeString()
      }]);
    } catch (err) {
      setLogs(prev => [...prev, {
        type: 'error',
        msg: `❌ Connection failed: ${err.message}`,
        time: new Date().toLocaleTimeString()
      }]);
    }
  };

  if (!visible) {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      maxHeight: collapsed ? '50px' : '50vh',
      backgroundColor: 'rgba(0,0,0,0.95)',
      color: '#0f0',
      fontFamily: 'monospace',
      fontSize: '11px',
      padding: '10px',
      zIndex: 999999,
      overflowY: 'auto',
      borderTop: collapsed ? '3px solid #0f0' : '2px solid #0f0',
      boxShadow: '0 -4px 12px rgba(0,255,0,0.3)',
      transition: 'max-height 0.3s ease',
      pointerEvents: 'auto'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: collapsed ? '0' : '10px',
        paddingBottom: collapsed ? '0' : '10px',
        borderBottom: collapsed ? 'none' : '1px solid #0f0'
      }}>
        <h3 style={{ margin: 0, color: '#0f0', fontSize: collapsed ? '16px' : '14px', fontWeight: 'bold' }}>
          🔍 DEBUG PANEL {collapsed ? '- TAP TO EXPAND' : ''}
        </h3>
        <div style={{ display: 'flex', gap: '5px' }}>
          <button
            onClick={() => setCollapsed(!collapsed)}
            style={{
              backgroundColor: '#ff9800',
              color: '#000',
              border: 'none',
              padding: '5px 10px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold'
            }}
          >
            {collapsed ? '▲' : '▼'}
          </button>
          <button
            onClick={() => setVisible(false)}
            style={{
              backgroundColor: '#f00',
              color: '#fff',
              border: 'none',
              padding: '5px 10px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {!collapsed && (<>

      <div style={{ marginBottom: '15px', color: '#fff' }}>
        <div><strong style={{ color: '#0ff' }}>Hostname:</strong> {window.location.hostname}</div>
        <div><strong style={{ color: '#0ff' }}>Port:</strong> {window.location.port}</div>
        <div><strong style={{ color: '#0ff' }}>Protocol:</strong> {window.location.protocol}</div>
        <div><strong style={{ color: '#0ff' }}>Backend URL:</strong> {BACKEND_URL}</div>
        <div><strong style={{ color: '#0ff' }}>REACT_APP_BACKEND_URL:</strong> {process.env.REACT_APP_BACKEND_URL || '(not set - using auto-detection)'}</div>
      </div>

      <button
        onClick={testConnection}
        style={{
          backgroundColor: '#0f0',
          color: '#000',
          border: 'none',
          padding: '10px 20px',
          borderRadius: '4px',
          cursor: 'pointer',
          fontWeight: 'bold',
          marginBottom: '15px',
          width: '100%'
        }}
      >
        Test Backend Connection
      </button>

      <div style={{ borderTop: '1px solid #0f0', paddingTop: '10px' }}>
        <strong style={{ color: '#ff0' }}>Console Logs:</strong>
        <button
          onClick={() => setLogs([])}
          style={{
            backgroundColor: '#666',
            color: '#fff',
            border: 'none',
            padding: '2px 8px',
            borderRadius: '3px',
            cursor: 'pointer',
            marginLeft: '10px',
            fontSize: '10px'
          }}
        >
          Clear
        </button>
        <div style={{ marginTop: '10px', maxHeight: '60vh', overflowY: 'auto' }}>
          {logs.length === 0 ? (
            <div style={{ color: '#888' }}>No logs yet...</div>
          ) : (
            logs.map((log, i) => (
              <div
                key={i}
                style={{
                  marginBottom: '5px',
                  color: log.type === 'error' ? '#f00' : '#0f0',
                  borderLeft: `3px solid ${log.type === 'error' ? '#f00' : '#0f0'}`,
                  paddingLeft: '5px'
                }}
              >
                <span style={{ color: '#888', fontSize: '10px' }}>[{log.time}]</span> {log.msg}
              </div>
            ))
          )}
        </div>
      </div>
      </>)}
    </div>
  );
}
